import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import StatusButton from './StatusButton';
import CollectionForm from './CollectionForm';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiPhone, FiNavigation, FiUser, FiCalendar, FiClock, FiMapPin, FiFileText } = FiIcons;

const STATUS_CONFIG = {
  assigned: { label: 'Assigned', color: 'bg-gray-100 text-gray-700' },
  driver_accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-700' },
  driver_on_the_way: { label: 'On The Way', color: 'bg-orange-100 text-orange-700' },
  driver_reached_pickup: { label: 'Reached', color: 'bg-purple-100 text-purple-700' },
  pickup_started: { label: 'Collecting', color: 'bg-teal-100 text-teal-700' },
  pickup_completed: { label: 'Completed ✓', color: 'bg-green-100 text-green-700' },
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showCollection, setShowCollection] = useState(false);

  const fetchJob = async () => {
    const { data, error: err } = await supabase
      .from('bookings')
      .select(`*, profiles:customer_id(full_name, phone)`)
      .eq('id', id)
      .single();
    if (!err) setJob(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchJob();
    const channel = supabase
      .channel(`job-${id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bookings', filter: `id=eq.${id}` },
        payload => setJob(prev => ({ ...prev, ...payload.new }))
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    setError('');
    try {
      const { error: err } = await supabase
        .from('bookings')
        .update({ status: newStatus })
        .eq('id', id);
      if (err) throw err;
      setJob(prev => ({ ...prev, status: newStatus }));
      if (newStatus === 'pickup_started') setShowCollection(true);
    } catch (e) {
      setError(e.message || 'Update failed. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCall = () => {
    const phone = job?.profiles?.phone;
    if (phone) window.location.href = `tel:${phone}`;
  };

  const handleMap = () => {
    const addr = job?.pickup_address;
    if (!addr) return;
    const lat = job.pickup_lat;
    const lng = job.pickup_lng;
    const query = lat && lng
      ? `${lat},${lng}`
      : encodeURIComponent(`${addr.line1}, ${addr.locality}, ${addr.city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const handleCollectionSubmit = () => {
    setJob(prev => ({ ...prev, status: 'pickup_completed' }));
    setShowCollection(false);
  };

  if (loading) {
    return (
      <div className="p-4 pt-8">
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-4 pt-8 text-center">
        <SafeIcon name="AlertCircle" className="text-5xl text-red-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">Job not found</p>
        <button onClick={() => navigate('/driver')} className="mt-4 text-brand-navy underline">Go Back</button>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.assigned;
  const addr = job.pickup_address || {};
  const isCompleted = job.status === 'pickup_completed';

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="bg-brand-navy text-white px-4 pt-10 pb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-300 mb-4 active:opacity-70">
          <FiArrowLeft /> Back
        </button>
        <div className="flex justify-between items-start">
          <div>
            <p className="text-gray-400 text-xs font-mono mb-1">{job.booking_number}</p>
            <h1 className="text-xl font-bold">{job.profiles?.full_name || 'Customer'}</h1>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${cfg.color}`}>
            {cfg.label}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
            <SafeIcon name="AlertCircle" className="shrink-0" /> {error}
          </div>
        )}

        {/* Action Buttons: Call + Map */}
        {!isCompleted && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleCall}
              className="flex items-center justify-center gap-2 bg-green-500 text-white py-3.5 rounded-2xl font-semibold text-sm active:scale-95 transition-transform shadow-sm"
            >
              <FiPhone className="text-lg" /> Call Customer
            </button>
            <button
              onClick={handleMap}
              className="flex items-center justify-center gap-2 bg-blue-500 text-white py-3.5 rounded-2xl font-semibold text-sm active:scale-95 transition-transform shadow-sm"
            >
              <FiNavigation className="text-lg" /> Open Maps
            </button>
          </div>
        )}

        {/* Customer Info */}
        <InfoCard icon={FiUser} title="Customer">
          <p className="font-semibold text-brand-navy">{job.profiles?.full_name}</p>
          <p className="text-sm text-gray-500">{job.profiles?.phone || 'No phone number'}</p>
        </InfoCard>

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-3">
          <InfoCard icon={FiCalendar} title="Pickup Date">
            <p className="font-semibold text-brand-navy">{job.pickup_date}</p>
          </InfoCard>
          <InfoCard icon={FiClock} title="Time Slot">
            <p className="font-semibold text-brand-navy text-sm">{job.pickup_slot}</p>
          </InfoCard>
        </div>

        {/* Address */}
        <InfoCard icon={FiMapPin} title="Pickup Address">
          <p className="font-semibold text-brand-navy">{addr.line1}</p>
          <p className="text-sm text-gray-500">{addr.locality}, {addr.city}</p>
          {addr.landmark && <p className="text-xs text-gray-400 mt-0.5">Near: {addr.landmark}</p>}
          {addr.pincode && <p className="text-xs text-gray-400">PIN: {addr.pincode}</p>}
        </InfoCard>

        {/* Notes */}
        {job.customer_notes && (
          <InfoCard icon={FiFileText} title="Customer Notes">
            <p className="text-sm text-gray-600">{job.customer_notes}</p>
          </InfoCard>
        )}

        {/* Collection Form */}
        {showCollection && (
          <CollectionForm
            bookingId={job.id}
            driverId={user.id}
            onSubmit={handleCollectionSubmit}
            onCancel={() => setShowCollection(false)}
          />
        )}

        {/* Completed Badge */}
        {isCompleted && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <SafeIcon name="CheckCircle" className="text-4xl text-green-500" />
            </div>
            <h3 className="font-bold text-green-700 text-lg">Pickup Completed!</h3>
            <p className="text-green-600 text-sm mt-1">Items have been collected successfully.</p>
          </div>
        )}
      </div>

      {/* Sticky Bottom Action Button */}
      {!showCollection && !isCompleted && (
        <div className="fixed bottom-16 left-0 right-0 px-4 pb-2">
          <StatusButton
            status={job.status}
            loading={updating}
            onUpdate={handleStatusUpdate}
          />
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Icon className="text-brand-gold" /> {title}
      </p>
      {children}
    </div>
  );
}